/**
 * Audio Store
 *
 * Zustand store wrapping the AudioEngine singleton.
 * Tracks current mood, active ambience layers, and volume levels.
 * GM controls what plays; commands broadcast to all players via Supabase realtime.
 */

import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { audioEngine, type BusName } from '../audio/audioEngine';
import { MOOD_TRACKS, AMBIENCE_LAYERS, SFX_ENTRIES } from '../audio/trackManifest';
import { supabase } from '../lib/supabase';
import type { RealtimeChannel } from '@supabase/supabase-js';

// =============================================================================
// AUDIO COMMAND TYPES (broadcast payload)
// =============================================================================

interface AudioCommand {
  type: 'mood' | 'layer-on' | 'layer-off' | 'layer-vol' | 'sfx' | 'bus-vol' | 'full-sync';
  moodId?: string | null;
  layerId?: string;
  sfxId?: string;
  bus?: BusName;
  volume?: number;
  // full-sync payload
  activeLayers?: string[];
  layerVolumes?: Record<string, number>;
  volumes?: { master: number; music: number; ambience: number; sfx: number };
}

// =============================================================================
// STATE
// =============================================================================

interface AudioState {
  isInitialized: boolean;
  isMuted: boolean;             // player's personal mute toggle (persisted)
  currentMood: string | null;   // mood track ID
  activeLayers: string[];       // ambience layer IDs
  layerVolumes: Record<string, number>; // per-layer volume overrides
  diceSoundsEnabled: boolean;   // play sounds on dice rolls
  volumes: {
    master: number;
    music: number;
    ambience: number;
    sfx: number;
  };

  // Broadcast state
  broadcastChannel: RealtimeChannel | null;
  isBroadcasting: boolean; // true = GM sending commands

  // Actions
  init: () => void;
  setMuted: (muted: boolean) => void;
  setMood: (moodId: string | null) => void;
  toggleLayer: (layerId: string) => void;
  setLayerVolume: (layerId: string, volume: number) => void;
  triggerSFX: (sfxId: string) => void;
  setVolume: (bus: BusName, volume: number) => void;
  setDiceSoundsEnabled: (enabled: boolean) => void;
  playDiceSound: () => void;

  // Broadcast
  startBroadcasting: (campaignId: string) => void;
  stopBroadcasting: () => void;
  subscribeToAudio: (campaignId: string) => void;
  unsubscribeFromAudio: () => void;
}

export const useAudioStore = create<AudioState>()(
  persist(
  (set, get) => ({
  isInitialized: false,
  isMuted: false,
  currentMood: null,
  activeLayers: [],
  layerVolumes: {},
  diceSoundsEnabled: true,
  volumes: {
    master: 0.7,
    music: 0.5,
    ambience: 0.6,
    sfx: 0.8,
  },
  broadcastChannel: null,
  isBroadcasting: false,

  init: () => {
    if (get().isInitialized) return;

    audioEngine.init();

    // Apply stored volumes (respect mute state)
    const { volumes, isMuted } = get();
    audioEngine.setVolume('master', isMuted ? 0 : volumes.master);
    audioEngine.setVolume('music', volumes.music);
    audioEngine.setVolume('ambience', volumes.ambience);
    audioEngine.setVolume('sfx', volumes.sfx);

    set({ isInitialized: true });
  },

  setMuted: (muted) => {
    set({ isMuted: muted });
    if (get().isInitialized) {
      audioEngine.setVolume('master', muted ? 0 : get().volumes.master);
    }
  },

  // ===========================================================================
  // MOOD
  // ===========================================================================

  setMood: (moodId) => {
    const { isInitialized, currentMood } = get();
    if (!isInitialized) return;
    if (moodId === currentMood) return;

    if (moodId === null) {
      audioEngine.stopMusic();
      set({ currentMood: null });
    } else {
      const track = MOOD_TRACKS.find((t) => t.id === moodId);
      if (!track) return;
      audioEngine.playMusic(track.url);
      set({ currentMood: moodId });
    }

    broadcast(get(), { type: 'mood', moodId });
  },

  // ===========================================================================
  // AMBIENCE LAYERS
  // ===========================================================================

  toggleLayer: (layerId) => {
    const { isInitialized, activeLayers, layerVolumes } = get();
    if (!isInitialized) return;

    if (activeLayers.includes(layerId)) {
      audioEngine.removeAmbienceLayer(layerId);
      set({ activeLayers: activeLayers.filter((id) => id !== layerId) });
      broadcast(get(), { type: 'layer-off', layerId });
    } else {
      const layer = AMBIENCE_LAYERS.find((l) => l.id === layerId);
      if (!layer) return;
      const vol = layerVolumes[layerId] ?? 0.6;
      audioEngine.addAmbienceLayer(layerId, layer.url, vol);
      set({ activeLayers: [...activeLayers, layerId] });
      broadcast(get(), { type: 'layer-on', layerId, volume: vol });
    }
  },

  setLayerVolume: (layerId, volume) => {
    const { layerVolumes } = get();
    audioEngine.setAmbienceLayerVolume(layerId, volume);
    set({ layerVolumes: { ...layerVolumes, [layerId]: volume } });
    broadcast(get(), { type: 'layer-vol', layerId, volume });
  },

  // ===========================================================================
  // SFX
  // ===========================================================================

  triggerSFX: (sfxId) => {
    const { isInitialized } = get();
    if (!isInitialized) return;

    const entry = SFX_ENTRIES.find((s) => s.id === sfxId);
    if (!entry) return;

    audioEngine.playSFX(entry.url);
    broadcast(get(), { type: 'sfx', sfxId });
  },

  // ===========================================================================
  // VOLUME
  // ===========================================================================

  setVolume: (bus, volume) => {
    // If setting master and muted, store the value but keep engine at 0
    if (bus === 'master' && get().isMuted) {
      set({ volumes: { ...get().volumes, master: volume } });
      return;
    }
    audioEngine.setVolume(bus, volume);
    set({ volumes: { ...get().volumes, [bus]: volume } });
    // Don't broadcast individual volume — player controls their own
  },

  // ===========================================================================
  // DICE SOUNDS (local only, not broadcast)
  // ===========================================================================

  setDiceSoundsEnabled: (enabled) => {
    set({ diceSoundsEnabled: enabled });
  },

  playDiceSound: () => {
    const { isInitialized, diceSoundsEnabled } = get();
    if (!isInitialized || !diceSoundsEnabled) return;
    audioEngine.playSFX('/audio/sfx/dice-roll.ogg');
  },

  // ===========================================================================
  // BROADCAST (GM side — sends commands to players)
  // ===========================================================================

  startBroadcasting: (campaignId: string) => {
    if (get().isBroadcasting) return; // already broadcasting

    const { broadcastChannel } = get();
    if (broadcastChannel) {
      // Channel already exists from subscribeToAudio — just enable sending
      console.log('[audio] broadcasting on existing channel', `audio-${campaignId}`);
      set({ isBroadcasting: true });
      return;
    }

    const channel = supabase.channel(`audio-${campaignId}`);
    channel.subscribe((status) => {
      if (status === 'SUBSCRIBED') {
        console.log('[audio] broadcasting on', `audio-${campaignId}`);
        set({ isBroadcasting: true });
      }
    });

    set({ broadcastChannel: channel });
  },

  stopBroadcasting: () => {
    const { broadcastChannel } = get();
    if (broadcastChannel) {
      supabase.removeChannel(broadcastChannel);
      set({ broadcastChannel: null, isBroadcasting: false });
    }
  },

  // ===========================================================================
  // SUBSCRIBE (player side — receives commands from GM)
  // ===========================================================================

  subscribeToAudio: (campaignId: string) => {
    get().unsubscribeFromAudio();

    const channel = supabase
      .channel(`audio-${campaignId}`)
      .on('broadcast', { event: 'audio-cmd' }, (payload) => {
        const cmd = payload.payload as AudioCommand;
        handleRemoteCommand(cmd);
      })
      .subscribe((status) => {
        if (status === 'SUBSCRIBED') {
          console.log('[audio] subscribed to', `audio-${campaignId}`);
        }
      });

    set({ broadcastChannel: channel });
  },

  unsubscribeFromAudio: () => {
    const { broadcastChannel } = get();
    if (broadcastChannel) {
      supabase.removeChannel(broadcastChannel);
      set({ broadcastChannel: null });
    }
  },
}),
  {
    name: 'machina-audio',
    partialize: (state) => ({
      volumes: state.volumes,
      diceSoundsEnabled: state.diceSoundsEnabled,
      isMuted: state.isMuted,
    }),
  }
));

// =============================================================================
// HELPERS
// =============================================================================

/** Send an audio command to all players via Supabase broadcast */
function broadcast(state: AudioState, cmd: AudioCommand) {
  if (!state.isBroadcasting || !state.broadcastChannel) return;

  state.broadcastChannel.send({
    type: 'broadcast',
    event: 'audio-cmd',
    payload: cmd,
  });
}

/**
 * Handle a remote audio command (player side).
 * Applies changes directly to the audioEngine without re-broadcasting.
 */
function handleRemoteCommand(cmd: AudioCommand) {
  const state = useAudioStore.getState();

  // Auto-init on first GM command (page has been loaded = user has interacted)
  if (!state.isInitialized) {
    state.init();
  }

  // Respect player mute — still update store state so unmuting restores correctly
  // but don't block the state updates (audio engine handles mute via master=0)

  switch (cmd.type) {
    case 'mood': {
      if (cmd.moodId === null) {
        audioEngine.stopMusic();
        useAudioStore.setState({ currentMood: null });
      } else {
        const track = MOOD_TRACKS.find((t) => t.id === cmd.moodId);
        if (track) {
          audioEngine.playMusic(track.url);
          useAudioStore.setState({ currentMood: cmd.moodId ?? null });
        }
      }
      break;
    }
    case 'layer-on': {
      const layer = AMBIENCE_LAYERS.find((l) => l.id === cmd.layerId);
      if (layer && cmd.layerId && !state.activeLayers.includes(cmd.layerId)) {
        audioEngine.addAmbienceLayer(cmd.layerId, layer.url, cmd.volume ?? 0.6);
        useAudioStore.setState({
          activeLayers: [...state.activeLayers, cmd.layerId],
          layerVolumes: { ...state.layerVolumes, [cmd.layerId]: cmd.volume ?? 0.6 },
        });
      }
      break;
    }
    case 'layer-off': {
      if (cmd.layerId) {
        audioEngine.removeAmbienceLayer(cmd.layerId);
        useAudioStore.setState({
          activeLayers: state.activeLayers.filter((id) => id !== cmd.layerId),
        });
      }
      break;
    }
    case 'layer-vol': {
      if (cmd.layerId && cmd.volume !== undefined) {
        audioEngine.setAmbienceLayerVolume(cmd.layerId, cmd.volume);
        useAudioStore.setState({
          layerVolumes: { ...state.layerVolumes, [cmd.layerId]: cmd.volume },
        });
      }
      break;
    }
    case 'sfx': {
      const entry = SFX_ENTRIES.find((s) => s.id === cmd.sfxId);
      if (entry) {
        audioEngine.playSFX(entry.url);
      }
      break;
    }
    case 'full-sync': {
      // Stop all current audio
      audioEngine.stopMusic();
      for (const layerId of state.activeLayers) {
        audioEngine.removeAmbienceLayer(layerId);
      }

      // Apply synced state
      if (cmd.moodId) {
        const track = MOOD_TRACKS.find((t) => t.id === cmd.moodId);
        if (track) audioEngine.playMusic(track.url);
      }

      const layers = cmd.activeLayers ?? [];
      const vols = cmd.layerVolumes ?? {};
      for (const layerId of layers) {
        const layer = AMBIENCE_LAYERS.find((l) => l.id === layerId);
        if (layer) {
          audioEngine.addAmbienceLayer(layerId, layer.url, vols[layerId] ?? 0.6);
        }
      }

      useAudioStore.setState({
        currentMood: cmd.moodId ?? null,
        activeLayers: layers,
        layerVolumes: vols,
      });
      break;
    }
  }
}

// =============================================================================
// AUTO-INIT: initialize audio on first user interaction (click/keydown/touch)
// Browser autoplay policy requires a user gesture to create AudioContext.
// =============================================================================

function autoInitAudio() {
  const state = useAudioStore.getState();
  if (state.isInitialized) return;
  state.init();
  // Remove listeners after first init
  document.removeEventListener('click', autoInitAudio);
  document.removeEventListener('keydown', autoInitAudio);
  document.removeEventListener('touchstart', autoInitAudio);
}

if (typeof document !== 'undefined') {
  document.addEventListener('click', autoInitAudio, { once: true });
  document.addEventListener('keydown', autoInitAudio, { once: true });
  document.addEventListener('touchstart', autoInitAudio, { once: true });
}
